// Business Brain Memory Constants
// Single source of truth for memory-related constants and types

import {
  QUICK_START_FIELDS,
  CORE_QUICK_START_FIELDS,
  TOTAL_CORE_FIELDS,
  TOTAL_QUICK_START_FIELDS,
  getNextIncompleteField,
  getNextIncompleteFieldOrder,
  getQuickStartFieldByKey,
  getConversationalPrompt,
  normalizeFieldKey,
  type QuickStartField,
} from "@/core/constants/quick-start-fields"

export const STORAGE_KEY = "biziq_business_brain"
export const KEY_DETAILS_STORAGE_KEY = "biziq_key_details_completed"

export const CORE_FIELDS = CORE_QUICK_START_FIELDS.map((field, index) => ({
  id: String(index + 1),
  fieldId: field.key,
  label: field.label,
  prompt: field.conversationalPrompt.replace("{business_name}", "your business"),
  placeholder: field.placeholder ?? `Enter ${field.label.toLowerCase()}...`,
  section: getCoreFieldSection(field.key),
  category: getCoreFieldCategory(field.key),
  categoryLabel: getCoreFieldCategoryLabel(field.key),
  sectionLabel: field.label,
})) as const

// Helper to map field keys to sections (backward compat)
function getCoreFieldSection(key: string): string {
  switch (key) {
    case "business_name":
      return "identity"
    case "what_you_do":
      return "offerings"
    case "who_you_serve":
      return "audience"
    case "biggest_challenge":
      return "strategy"
    case "primary_goal":
      return "strategy"
    default:
      return "general"
  }
}

function getCoreFieldCategory(key: string): string {
  switch (key) {
    case "business_name":
      return "business"
    case "what_you_do":
      return "offerings"
    case "who_you_serve":
      return "audience"
    case "biggest_challenge":
      return "strategy"
    case "primary_goal":
      return "strategy"
    default:
      return "general"
  }
}

function getCoreFieldCategoryLabel(key: string): string {
  switch (key) {
    case "business_name":
      return "Business"
    case "what_you_do":
      return "Offerings"
    case "who_you_serve":
      return "Audience"
    case "biggest_challenge":
      return "Strategy"
    case "primary_goal":
      return "Strategy"
    default:
      return "General"
  }
}

export const ALL_QUICK_START_FIELDS = QUICK_START_FIELDS

export type CoreFieldId = (typeof CORE_FIELDS)[number]["fieldId"]

export interface Memory {
  id: string
  field: string
  label: string
  value: string
  section: string
  sectionLabel?: string
  category?: string
  categoryLabel?: string
  timestamp: number
  isArchived?: boolean
  confidence?: number
}

export interface CoreField {
  id: string
  fieldId: string
  label: string
  prompt: string
  placeholder: string
  section: string
  category: string
  categoryLabel: string
  sectionLabel: string
}

// Re-export for convenience
export { TOTAL_CORE_FIELDS, TOTAL_QUICK_START_FIELDS, getConversationalPrompt, normalizeFieldKey, type QuickStartField }

// Helper to get field metadata by fieldId
export function getFieldMetadata(fieldId: string): CoreField | undefined {
  const normalized = normalizeFieldKey(fieldId)
  return CORE_FIELDS.find((f) => f.fieldId === normalized) as CoreField | undefined
}

export function getFieldByIndex(index: number): CoreField | undefined {
  return CORE_FIELDS[index] as CoreField | undefined
}

export function getNextIncompleteFieldIndex(completedFields: string[]): number {
  // Normalize completed field keys for backward compat
  const normalizedCompleted = completedFields.map(normalizeFieldKey)
  return getNextIncompleteFieldOrder(normalizedCompleted, true) // core only
}

export function getNextIncompleteFieldIndexAll(completedFields: string[]): number {
  const normalizedCompleted = completedFields.map(normalizeFieldKey)
  return getNextIncompleteFieldOrder(normalizedCompleted, false) // all fields
}

export function getNextQuickStartField(completedFields: string[], coreOnly = false): QuickStartField | undefined {
  const normalizedCompleted = completedFields.map(normalizeFieldKey)
  return getNextIncompleteField(normalizedCompleted, coreOnly)
}

export function areCoreFieldsComplete(completedFields: string[]): boolean {
  const normalizedCompleted = completedFields.map(normalizeFieldKey)
  return CORE_QUICK_START_FIELDS.every((f) => normalizedCompleted.includes(f.key))
}

export function getCompletionCounts(completedFields: string[]): {
  coreCompleted: number
  coreTotal: number
  allCompleted: number
  allTotal: number
} {
  const normalizedCompleted = completedFields.map(normalizeFieldKey)
  const coreCompleted = CORE_QUICK_START_FIELDS.filter((f) => normalizedCompleted.includes(f.key)).length
  const allCompleted = QUICK_START_FIELDS.filter((f) => normalizedCompleted.includes(f.key)).length

  return {
    coreCompleted,
    coreTotal: TOTAL_CORE_FIELDS,
    allCompleted,
    allTotal: TOTAL_QUICK_START_FIELDS,
  }
}

// Helper to generate formatted "Next Question" message block
export function generateNextQuestionBlock(nextFieldIndex: number, businessName?: string): string {
  const field = CORE_FIELDS[nextFieldIndex]
  if (!field) return ""

  const memoryNumber = nextFieldIndex + 1
  const quickStartField = getQuickStartFieldByKey(field.fieldId)

  // Use conversational prompt from quick-start-fields
  const questionText = quickStartField ? getConversationalPrompt(quickStartField, businessName) : field.prompt

  const guidanceText = quickStartField?.helperText ?? ""
  const tipText = quickStartField?.tipText ?? ""

  return `## Memory ${memoryNumber} of ${TOTAL_CORE_FIELDS} · ${field.label}

**${questionText}**

${guidanceText ? `*${guidanceText}*` : ""}

${tipText ? `> ${tipText}` : ""}
`
}

export function generateExtendedQuestionBlock(fieldOrder: number, businessName?: string): string {
  const field = QUICK_START_FIELDS.find((f) => f.order === fieldOrder)
  if (!field) return ""

  const questionText = getConversationalPrompt(field, businessName)
  const guidanceText = field.helperText ?? ""
  const tipText = field.tipText ?? ""

  return `## Memory ${fieldOrder} of ${TOTAL_QUICK_START_FIELDS} · ${field.label}

**${questionText}**

${guidanceText ? `*${guidanceText}*` : ""}

${tipText ? `> ${tipText}` : ""}
`
}

export function generateSavedMemoryBlock(field: CoreField, value: string, memoryNumber: number): string {
  return `### Saved Business Memory

- **${field.label}**: ${value}
`
}

// Memory event name for cross-component sync
export const MEMORY_CHANGED_EVENT = "bizziq:memory-changed"
